import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import { ChevronDownIcon } from "@heroicons/react/20/solid"
import { CheckIcon } from "@heroicons/react/24/outline"
import { api } from "api"
import LANGUAGES from "assets/data/language.data.json"
import classNames from "classnames"
import { Button } from "components/Button/Button"
import { CountryFlagRound } from "components/CountryFlag/CountryFlag"
import { useLocaleWithSetter } from "hooks/useLocale"
import { FC, MouseEvent, useContext, useRef } from "react"
import { AuthType } from "store/auth/Auth.interface"
import { AuthContext } from "store/auth/AuthContext"

import { Locale } from "@shared/enums/general"

interface LanguageSwitchProps {
  addArrow?: boolean
}

export const LanguageSwitch: FC<LanguageSwitchProps> = ({ addArrow }) => {
  const { locale, setLocale } = useLocaleWithSetter()
  const { type } = useContext(AuthContext) ?? {}
  const { mutate: saveLocale } = api.user.editLocale.useMutation()
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const selectedLang = LANGUAGES.find((a) => a.locale === locale)

  const handleSetLanguage = (e: MouseEvent<HTMLButtonElement>) => {
    const value = e.currentTarget.value as Locale
    setLocale(value)

    if (type === AuthType.Authenticated) {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => saveLocale([{ locale: value }]), 500)
    }
  }

  return (
    <Menu as="div" className="relative z-50 flex">
      <MenuButton
        as={Button}
        variant="secondary"
        impact="none"
        className="min-w-[60px] !px-1"
        suffixIcon={addArrow && <ChevronDownIcon className="size-4 text-gray-500 transition ui-open:rotate-180" />}
      >
        {selectedLang && <CountryFlagRound language={selectedLang} />}
      </MenuButton>
      <MenuItems transition className="dropdown-menu" modal={false}>
        {LANGUAGES.map((language, i) => (
          <MenuItem key={i}>
            <button
              id={`lang-selector-${language.locale}`}
              className="group flex w-full items-center gap-x-2 px-4 py-3 ui-active:bg-blue-50"
              onClick={handleSetLanguage}
              value={language.locale}
            >
              <CountryFlagRound language={language} />
              {language.longName}

              <CheckIcon
                className={classNames(
                  "ms-auto size-5 text-blue-500",
                  locale === language.locale ? "ui-selected:opacity-100" : "opacity-0"
                )}
              />
            </button>
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  )
}
